import { usePostV4ManufacturerByRouteManufacturerIdProductAndProductNumberFieldNameMutation } from "randmar-api-client";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, useToast } from "randmarcomps";
import { inject, observer } from "mobx-react";
import React, { useState, useEffect } from "react";
import WithManufacturer from "../../contexts/WithManufacturer";
import { Product } from "../../stores/product/Product";
import { RootStore } from "../../stores/rootStore";
import CategorySelector from "./CategorySelector";
import InlineSpinner from "../util/InlineSpinner";

interface IProps {
  store?: RootStore;
  applicationId: string;
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EditCategory: React.FC<IProps> = ({ store, applicationId, product, open, onOpenChange }) => {
  const { toast } = useToast();
  const [updateProduct, { isLoading }] = usePostV4ManufacturerByRouteManufacturerIdProductAndProductNumberFieldNameMutation();

  const [category, setCategory] = useState(product.category || "");

  useEffect(() => {
    if (open) {
      setCategory(product.category || "");
    }
  }, [open, product.category]);

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleConfirmRequest = async () => {
    const { productStore } = store!;
    try {
      await updateProduct({
        routeManufacturerId: applicationId,
        productNumber: product.randmarSKU || "",
        fieldName: "Category",
        newValue: category?.toString(),
      }).unwrap();
      toast({ variant: "success", title: "Success", description: "Category updated." });
      onOpenChange(false);
      await productStore.getProduct(applicationId, product.randmarSKU || "");
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to update category." });
    }
  };

  const propertyChange = (newValue: string) => {
    setCategory(newValue);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen && !isLoading) {
        onOpenChange(false);
      }
    }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">Edit Product Category</DialogTitle>
        </DialogHeader>
        <div hidden={isLoading}>
          <div className="py-4">
            <CategorySelector
              selectedCategory={category}
              onSelectedCategoryChange={(e: string) => propertyChange(e)}
            />
          </div>
          <DialogFooter>
            <div className="flex justify-between w-full">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                title="Confirm changes"
                onClick={handleConfirmRequest}
                disabled={category === product.category || isLoading}
              >
                Confirm
              </Button>
            </div>
          </DialogFooter>
        </div>
        <div hidden={!isLoading} className="flex flex-col items-center justify-center p-8">
          <p className="mb-2">Please wait...</p>
          <InlineSpinner size="lg" tone="accent" decorative />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WithManufacturer(inject("store")(observer(EditCategory)));
